/**
 * The browser's connection to a dedicated server, and the predicted world it
 * drives.
 *
 * Two things live here, and they are one file because the seam between them is
 * two method calls wide:
 *
 *   `NetClient`     the connection. Handshake, message routing, room state,
 *                   snapshots into the prediction, inputs out, RTT, events out.
 *   `NetWorldView`  the same shape src/main.ts's renderer already consumes
 *                   (`WorldView`), backed by the prediction and the snapshot
 *                   interpolator instead of by a local `World`.
 *
 * That second class is the whole reason the networked and offline games share a
 * renderer, a HUD, a camera and an effects system COMPLETELY rather than
 * approximately. `App` asks a `WorldView` for cars, a ball, a match and a step;
 * it is never told which kind it has.
 *
 * ===========================================================================
 * NOTHING HERE REIMPLEMENTS PREDICTION
 * ===========================================================================
 *
 * `PredictionClient`, `SnapshotInterpolator`, `CorrectionSmoother` and
 * `TickLead` (./prediction) are built and tested; this file OWNS NONE OF THAT.
 * It wires them: snapshots go in through `SnapshotHistory.resolve` (which is
 * what makes a delta whose base is gone a dropped frame rather than a
 * corruption), the resolved keyframe goes to both the reconciler and the
 * interpolator, and the tick-lead controller decides how many ticks a frame is
 * worth.
 *
 * The one rule that file states and that is easy to break from here: READ
 * `PredictionClient.world` FRESH EVERY TIME. A reconciliation rebuilds the world
 * from a serialized state, so the object identity changes whenever a correction
 * lands. Every access below goes through the getter.
 *
 * ===========================================================================
 * WHERE RTT ACTUALLY COMES FROM
 * ===========================================================================
 *
 * The server measures round trips with RFC 6455 ping/pong (server/ws-transport)
 * and a browser gives page JavaScript no way to see those frames. So the client
 * has three sources and prefers them in this order:
 *
 *   1. `RoomState.players[me].ping` — the SERVER's own measurement, refreshed
 *      once a second. A true round trip, and free.
 *   2. `ServerMessage.Ping` -> `ClientMessage.Pong`. The server does not send
 *      these over WebSocket, but a WebRTC DataChannel has no protocol-level
 *      ping and will need them, so they are answered and their payload is
 *      echoed. A ping carrying `rttMs` is adopted directly.
 *   3. The input-ack round trip: the time between sending input sequence S and
 *      seeing a snapshot whose `ackedInputSeq` reaches S. Always available,
 *      including before the first `RoomState`, and it is what the tick-lead
 *      controller runs on while a room is filling.
 *
 * Source 3 is deliberately NOT corrected for the server's snapshot cadence, and
 * that inflates it by up to one snapshot interval (33 ms at the default rate).
 * That is the safe direction: `TickLead` turns a larger RTT into a larger lead,
 * a lead that is slightly too large costs a few milliseconds of input latency
 * nobody can feel, and a lead that is too small costs dropped inputs, which
 * everybody feels. It is reported as `ackLatencyMs` rather than as "the ping",
 * so the number on the scoreboard is never this one when a real one exists.
 *
 * ===========================================================================
 * FAILURE IS THE NORMAL CASE
 * ===========================================================================
 *
 * Every path out of this class that is not "playing" ends in a `NetFailure`
 * with a message a player can read, and the phase becomes `closed` exactly
 * once. Refused, full, version-mismatched, kicked, dropped mid-match: all of
 * them are the same shape, because the UI must never have a fifth branch that
 * shows nothing. A truncated or malformed frame is counted and DROPPED — a
 * hostile or broken peer must not be able to choose when this throws.
 *
 * A protocol-version mismatch refuses to play rather than degrading. A snapshot
 * from a server that numbers its fields differently decodes into entirely
 * plausible garbage, and the symptom is "the physics is broken" — see the note
 * on `PROTOCOL_VERSION` in ./protocol.
 */

import { makeCar, neutralInput, type CarInput, type CarState } from '../physics/car-state';
import { makeBall, type BallState } from '../physics/ball';
import { defaultMutators, type MutatorConfig } from '../physics/mutators';
import { carConfigFor, CAR_BODIES, type CarBody } from '../physics/car-config';
import { clone as cloneVec, type Vec3 } from '../physics/math';
import { TICK_RATE } from '../physics/constants';
import type { MatchPhase, MatchState } from '../game/match';
import type { ArenaGeometry } from '../physics/surface';
import { World } from '../sim/world';
import {
  FLAG_DEMOLISHED,
  FLAG_HAS_FLIP,
  FLAG_IS_DODGING,
  FLAG_JUMPED,
  FLAG_ON_GROUND,
  FLAG_SUPERSONIC,
  decodeSnapshot,
  encodeControl,
  encodeInputPacket,
  messageType,
  decodeControl,
} from './codec';
import { applySnapshot, SnapshotHistory } from './snapshot';
import {
  InterpolationClock,
  PredictionClient,
  SnapshotInterpolator,
  TickLead,
  type InterpolatedCar,
  type InterpolatedFrame,
} from './prediction';
import { isMultiplayerMode, matchConfigForMode, modeRules } from './modes';
import {
  ClientMessage,
  DEFAULT_SNAPSHOT_RATE,
  PROTOCOL_VERSION,
  ServerMessage,
  type MultiplayerMode,
  type NetEvent,
  type RoomPlayer,
  type RoomState,
  type Snapshot,
  type Transport,
} from './protocol';
import {
  LobbyMessage,
  type ChatRelayPayload,
  type EventsPayload,
  type JoinRoomPayload,
  type KickCode,
  type KickPayload,
  type QueueStatusPayload,
  type RoomClosedPayload,
  type WelcomeExtended,
} from './control';

// ---------------------------------------------------------------------------
// Phases and failures
// ---------------------------------------------------------------------------

export type NetPhase =
  /** Constructed, nothing opened. */
  | 'idle'
  /** Socket opening. */
  | 'connecting'
  /** Hello sent, waiting for Welcome. */
  | 'handshake'
  /** Seated in a room that has not started. */
  | 'lobby'
  /** Waiting in the quick-match queue. */
  | 'queued'
  /** The match is running and the prediction is live. */
  | 'playing'
  /** Over, for whatever reason. `failure` says which. */
  | 'closed';

/**
 * Why a session ended. Every one of these has a sentence on screen; see
 * `failureMessage`. `null` alongside `closed` means the player left on purpose.
 */
export type NetFailure =
  | 'refused'
  | 'protocol-version'
  | 'server-full'
  | 'room-full'
  | 'room-closed'
  | 'no-such-room'
  | 'bad-request'
  | 'timeout'
  | 'shutdown'
  | 'auth'
  | 'lost';

const KICK_FAILURES: Readonly<Record<KickCode, NetFailure>> = {
  'protocol-version': 'protocol-version',
  'server-full': 'server-full',
  'room-full': 'room-full',
  'room-closed': 'room-closed',
  'bad-request': 'bad-request',
  timeout: 'timeout',
  shutdown: 'shutdown',
  auth: 'auth',
};

/** One line a player can act on, per failure. Never a code on its own. */
export const failureMessage = (failure: NetFailure, detail = ''): string => {
  const base = ((): string => {
    switch (failure) {
      case 'refused':
        return 'Could not reach the server. Check the address and that it is running.';
      case 'protocol-version':
        return `This build speaks protocol ${PROTOCOL_VERSION} and the server does not. Update one of them — playing anyway would look like broken physics.`;
      case 'server-full':
        return 'The server is full. Try again in a moment.';
      case 'room-full':
        return 'That match is full.';
      case 'room-closed':
        return 'The match ended or was closed.';
      case 'no-such-room':
        return 'No match with that code. Codes expire when the room does.';
      case 'bad-request':
        return 'The server rejected this client.';
      case 'timeout':
        return 'The server stopped hearing from this client.';
      case 'shutdown':
        return 'The server shut down.';
      case 'auth':
        return 'The server requires a signed-in account. Sign in and try again.';
      case 'lost':
        // Says the useful half out loud: the seat is held for thirty seconds
        // (see DEFAULT_RECONNECT_GRACE_TICKS), so this is usually survivable.
        return 'Connection lost. Your seat is held for a short while, so a reconnect may still get it back.';
    }
  })();
  return detail.length > 0 && detail !== base ? `${base} (${detail})` : base;
};

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/** What this client is asking the server for. One shape covers all four ways in. */
export interface NetIntent {
  mode: MultiplayerMode;
  /** A room code to join, or null. */
  code: string | null;
  /** Make a new room rather than joining or queueing. */
  create: boolean;
  /** Preferred team; ignored by the server when it would unbalance the room. */
  team?: 0 | 1;
  spectator?: boolean;
}

/** A slot the server is still holding for us, from a previous session. */
export interface ReconnectTicket {
  code: string;
  playerId: number;
  token: string;
}

export interface NetListener {
  /** Any phase change, including into `closed`. */
  onPhase?(phase: NetPhase): void;
  onRoomState?(state: RoomState): void;
  /** Server-truth events for the tick they happened on. */
  onEvents?(events: readonly NetEvent[], tick: number): void;
  onChat?(chat: ChatRelayPayload): void;
  onQueue?(status: QueueStatusPayload): void;
  /** The predicted world exists and the match is live. */
  onMatchStart?(): void;
  onClosed?(failure: NetFailure | null, message: string): void;
}

export interface NetClientOptions {
  transport: Transport;
  displayName: string;
  car: CarBody;
  intent: NetIntent;
  /**
   * Monotonic milliseconds. REQUIRED, and not defaulted to `performance.now`.
   *
   * src/net is a directory both ends of the wire import and test/net/decoupling
   * enforces that none of it reads a clock: a wall clock inside the netcode is
   * one refactor away from being imported into the dedicated server, and a
   * module that COULD read the time eventually gets written as if it should.
   * The browser's clock is supplied one layer out, in src/ui/net-runtime.ts.
   */
  now: () => number;
  listener?: NetListener;
  /** Present it to reclaim a seat after a drop. */
  ticket?: ReconnectTicket | null;
  /** Snapshots per second the server is expected to send. Corrected by Welcome. */
  snapshotRate?: number;
  /** Login token from /api/login. A server with accounts enabled kicks a Hello
   *  that does not carry one. */
  authToken?: string | null;
}

export interface NetStats {
  /** Frames that could not be decoded at all. */
  malformed: number;
  /** Deltas whose base had aged out. Normal on a lossy link; see SnapshotHistory. */
  droppedSnapshots: number;
  snapshots: number;
  corrections: number;
  resets: number;
  replayedTicks: number;
  inputPackets: number;
  /** Ticks the prediction is running ahead of the newest snapshot. */
  lead: number;
  /** What `TickLead` is aiming for. */
  targetLead: number;
  /** Times the lead controller had to give up and jump. */
  resyncs: number;
}

// ---------------------------------------------------------------------------
// Round-trip estimate
// ---------------------------------------------------------------------------

/**
 * Send times for recent input sequences, so a snapshot's `ackedInputSeq` can be
 * turned into a latency.
 *
 * A flat ring keyed by sequence: an input is acked within a round trip or it is
 * never interesting again, so nothing here has to survive longer than the ring.
 */
class AckTimer {
  private readonly at: Float64Array;
  private readonly seq: Int32Array;

  constructor(private readonly size = 256) {
    this.at = new Float64Array(size);
    this.seq = new Int32Array(size).fill(-1);
  }

  private slot(seq: number): number {
    return ((seq % this.size) + this.size) % this.size;
  }

  mark(seq: number, now: number): void {
    if (seq < 0) return;
    const i = this.slot(seq);
    this.seq[i] = seq;
    this.at[i] = now;
  }

  /** Milliseconds since `seq` was sent, or -1 when it is not held. */
  elapsed(seq: number, now: number): number {
    if (seq < 0) return -1;
    const i = this.slot(seq);
    if (this.seq[i] !== seq) return -1;
    return Math.max(0, now - this.at[i]);
  }
}

/** Exponential smoothing. 0.15 settles in about twenty samples — half a second
 *  at 30 Hz — which tracks a route change without chasing a single spike. */
const RTT_SMOOTHING = 0.15;

/** Every JSON control frame this build understands. See `onFrame`. */
const CONTROL_TYPES: ReadonlySet<number> = new Set<number>([
  ServerMessage.Welcome,
  ServerMessage.RoomState,
  ServerMessage.Event,
  ServerMessage.Ping,
  ServerMessage.Kick,
  LobbyMessage.QueueStatus,
  LobbyMessage.RoomClosed,
  LobbyMessage.Chat,
]);

// ---------------------------------------------------------------------------
// The client
// ---------------------------------------------------------------------------

export class NetClient {
  readonly intent: NetIntent;

  private readonly transport: Transport;
  private readonly listener: NetListener;
  private readonly now: () => number;
  private readonly displayName: string;
  private readonly car: CarBody;
  private readonly ticket: ReconnectTicket | null;
  private readonly authToken: string | null;

  private state: NetPhase = 'idle';
  private failed: NetFailure | null = null;
  private failureText = '';

  private welcome: WelcomeExtended | null = null;
  private room: RoomState | null = null;
  private queue: QueueStatusPayload | null = null;
  private session: NetWorldView | null = null;
  private started = false;

  private readonly history = new SnapshotHistory();
  private readonly acks = new AckTimer();
  /** Round trip the server itself measured, from `RoomState`. -1 when unknown. */
  private serverRtt = -1;
  private ackLatency = -1;
  private lastSnapshotAt = -1;
  private snapshotIntervalMs = 1000 / DEFAULT_SNAPSHOT_RATE;

  private readonly counters = {
    malformed: 0,
    droppedSnapshots: 0,
    inputPackets: 0,
  };

  constructor(options: NetClientOptions) {
    this.transport = options.transport;
    this.listener = options.listener ?? {};
    this.now = options.now;
    this.displayName = options.displayName;
    this.car = CAR_BODIES.includes(options.car) ? options.car : 'octane';
    this.intent = options.intent;
    this.ticket = options.ticket ?? null;
    this.authToken = options.authToken ?? null;
    if (options.snapshotRate !== undefined && options.snapshotRate > 0) {
      this.snapshotIntervalMs = 1000 / options.snapshotRate;
    }
  }

  // -------------------------------------------------------------------------
  // Read side
  // -------------------------------------------------------------------------

  get phase(): NetPhase {
    return this.state;
  }

  /** True once the session has finished, for whatever reason. */
  get ended(): boolean {
    return this.state === 'closed';
  }

  get failure(): NetFailure | null {
    return this.failed;
  }

  get failureDetail(): string {
    return this.failureText;
  }

  get roomState(): RoomState | null {
    return this.room;
  }

  get queueStatus(): QueueStatusPayload | null {
    return this.queue;
  }

  get roomCode(): string | null {
    return this.welcome?.code ?? this.room?.code ?? null;
  }

  get playerId(): number | null {
    return this.welcome?.playerId ?? null;
  }

  /** Index into `World.cars` this client drives, or null while spectating. */
  get carIndex(): number | null {
    return this.welcome?.carIndex ?? null;
  }

  /** The seat this client can reclaim after a drop, or null. */
  get reconnectTicket(): ReconnectTicket | null {
    const welcome = this.welcome;
    if (welcome === null || welcome.code === null) return null;
    return { code: welcome.code, playerId: welcome.playerId, token: welcome.reconnectToken };
  }

  /** The renderer's view of the world, once there is one. */
  get view(): NetWorldView | null {
    return this.session;
  }

  /** The local player's row in the room, if the roster has arrived. */
  get localPlayer(): RoomPlayer | null {
    const id = this.playerId;
    if (id === null || this.room === null) return null;
    return this.room.players.find(p => p.playerId === id) ?? null;
  }

  /**
   * The round trip the scoreboard shows: the server's measurement when it has
   * one, else this client's own ack-derived estimate, else zero. See the header.
   */
  get rttMs(): number {
    if (this.serverRtt >= 0) return this.serverRtt;
    if (this.ackLatency >= 0) return this.ackLatency;
    return 0;
  }

  /** The self-measured figure, kept separate so the two never get confused. */
  get ackLatencyMs(): number {
    return this.ackLatency;
  }

  /** Milliseconds since the last snapshot landed, or -1 before the first. */
  staleMs(): number {
    return this.lastSnapshotAt < 0 ? -1 : Math.max(0, this.now() - this.lastSnapshotAt);
  }

  /**
   * True when the link is bad enough that the player should be told.
   *
   * Three independent symptoms, because they have three different causes and a
   * single threshold would miss two of them: a long round trip (distance), a
   * snapshot gap (loss or a stalled server), and repeated resyncs (a clock that
   * cannot keep a lead). Four snapshot intervals is the gap the interpolator's
   * buffer cannot cover, i.e. the point at which the world visibly freezes.
   */
  get degraded(): boolean {
    if (this.state !== 'playing') return false;
    const stale = this.staleMs();
    if (stale >= 0 && stale > this.snapshotIntervalMs * 4) return true;
    if (this.rttMs > 200) return true;
    return (this.session?.resyncCount ?? 0) > 0 && (this.session?.leadTicks ?? 0) < 0;
  }

  get stats(): NetStats {
    const prediction = this.session?.predictionStats;
    return {
      malformed: this.counters.malformed,
      droppedSnapshots: this.counters.droppedSnapshots,
      snapshots: prediction?.snapshots ?? 0,
      corrections: prediction?.corrections ?? 0,
      resets: prediction?.resets ?? 0,
      replayedTicks: prediction?.replayedTicks ?? 0,
      inputPackets: this.counters.inputPackets,
      lead: this.session?.leadTicks ?? 0,
      targetLead: this.session?.targetLeadTicks ?? 0,
      resyncs: this.session?.resyncCount ?? 0,
    };
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  /**
   * Wire the transport up and say Hello.
   *
   * Hello goes out immediately rather than on an `open` event: `Transport` has
   * no such event by design, and `WebSocketTransport` buffers until the socket
   * opens. That keeps this class free of any notion of "connecting", which a
   * DataChannel would define differently anyway.
   */
  start(): void {
    if (this.state !== 'idle') return;
    this.setPhase('connecting');
    this.transport.onMessage(data => this.onFrame(data));
    this.transport.onClose(reason => this.onTransportClosed(reason));
    // `onClose` replays an already-finished transport, so a socket that failed
    // before this line ran has already closed the session. Read through the
    // getter: TypeScript narrows `this.state` across the guard above and would
    // otherwise answer from the phase this method started in.
    if (this.ended) return;

    this.send(ClientMessage.Hello, {
      protocolVersion: PROTOCOL_VERSION,
      displayName: this.displayName,
      car: this.car,
      ...(this.authToken === null ? {} : { authToken: this.authToken }),
    });
    this.setPhase('handshake');
  }

  /**
   * Leave the room and close, deliberately. No failure is recorded.
   *
   * ORDER MATTERS AND IS THE SAME EVERYWHERE THIS CLASS ENDS A SESSION: say the
   * last thing, record the outcome, THEN close the transport. Closing first
   * runs the transport's close handler synchronously, which lands in
   * `onTransportClosed` and files the outcome as a dropped link — so a clean
   * leave would report itself as a crash and the UI would offer to reconnect to
   * a room the player just walked out of.
   */
  leave(): void {
    if (this.state === 'closed') return;
    this.send(ClientMessage.LeaveRoom, null);
    this.finish(null, 'left');
    this.transport.close('left');
  }

  /** Drop everything without telling the server. For teardown paths. */
  dispose(): void {
    const wasOpen = !this.ended;
    this.finish(null, 'disposed');
    if (wasOpen) this.transport.close('disposed');
  }

  setReady(ready: boolean): void {
    if (this.state !== 'lobby' && this.state !== 'playing') return;
    this.send(ClientMessage.Ready, { ready });
  }

  /**
   * Ask for the other team.
   *
   * The protocol has no "change team" message, and deliberately: a seat is
   * assigned by `ServerRoom.addPlayer`, which is the only place the balancer
   * runs. So a switch is a leave and a re-join with `team` set — the server then
   * either honours it or refuses it for balance, and the truth arrives in the
   * next `RoomState`. Nothing here assumes it worked.
   *
   * Refused outright once the match is running: taking a seat mid-match would
   * hand the player a different car, and the room would rightly treat it as a
   * new join.
   */
  switchTeam(): void {
    const room = this.room;
    const player = this.localPlayer;
    if (this.state !== 'lobby' || room === null || player === null) return;
    if (room.inProgress || !modeRules(room.mode).teams) return;
    const code = this.roomCode;
    if (code === null) return;

    this.send(ClientMessage.LeaveRoom, null);
    this.sendJoin({ code, mode: room.mode, create: false, team: player.team === 0 ? 1 : 0 });
  }

  sendChat(text: string): void {
    if (this.state === 'closed' || text.trim().length === 0) return;
    this.send(ClientMessage.Chat, { text });
  }

  // -------------------------------------------------------------------------
  // Inbound
  // -------------------------------------------------------------------------

  /**
   * Route one frame.
   *
   * Every decode is inside a try. A truncated control frame, a snapshot cut
   * short by a half-written socket buffer and a frame from a client this server
   * has never met all arrive here, and none of them may throw out of a socket
   * callback — see the malformed-frame counter, which is the whole response.
   */
  private onFrame(data: ArrayBuffer): void {
    let type: number;
    try {
      type = messageType(data);
    } catch {
      this.counters.malformed++;
      return;
    }

    if (type === ServerMessage.Snapshot) {
      let snap: Snapshot;
      try {
        snap = decodeSnapshot(data);
      } catch {
        this.counters.malformed++;
        return;
      }
      this.onSnapshot(snap);
      return;
    }

    // An unrecognised TYPE is checked before the body is decoded, and that
    // order is the whole point: a message this build has not been taught is a
    // server from the future and costs nothing to ignore, while an undecodable
    // BODY is a broken or hostile peer. Decoding first would file the former as
    // the latter and count a malformed frame for every one of them. Same
    // distinction `decodeClientFrame` draws in server/wire.ts.
    if (!CONTROL_TYPES.has(type)) return;

    let payload: unknown;
    try {
      payload = decodeControl(data).payload;
    } catch {
      this.counters.malformed++;
      return;
    }

    switch (type) {
      case ServerMessage.Welcome:
        this.onWelcome(payload);
        return;
      case ServerMessage.RoomState:
        this.onRoomState(payload);
        return;
      case ServerMessage.Event:
        this.onEvents(payload);
        return;
      case ServerMessage.Ping:
        this.onPing(payload);
        return;
      case ServerMessage.Kick:
        this.onKick(payload);
        return;
      case LobbyMessage.QueueStatus:
        this.onQueueStatus(payload);
        return;
      case LobbyMessage.RoomClosed:
        this.onRoomClosed(payload);
        return;
      case LobbyMessage.Chat:
        if (isRecord(payload) && typeof payload.text === 'string') {
          this.listener.onChat?.(payload as unknown as ChatRelayPayload);
        }
        return;
      default:
        // Unreachable: `CONTROL_TYPES` above is exactly this switch's cases.
        return;
    }
  }

  private onWelcome(payload: unknown): void {
    if (!isRecord(payload)) {
      this.counters.malformed++;
      return;
    }
    if (payload.protocolVersion !== PROTOCOL_VERSION) {
      // Refuse to play. See the header, and PROTOCOL_VERSION in ./protocol.
      this.finish('protocol-version', `server speaks protocol ${String(payload.protocolVersion)}`);
      this.transport.close('protocol mismatch');
      return;
    }

    const welcome: WelcomeExtended = {
      protocolVersion: PROTOCOL_VERSION,
      playerId: numberOr(payload.playerId, 0),
      carIndex: payload.carIndex === null ? null : numberOr(payload.carIndex, null),
      serverTickRate: numberOr(payload.serverTickRate, TICK_RATE),
      snapshotRate: numberOr(payload.snapshotRate, DEFAULT_SNAPSHOT_RATE),
      code: typeof payload.code === 'string' ? payload.code : null,
      reconnectToken: typeof payload.reconnectToken === 'string' ? payload.reconnectToken : '',
      seed: numberOr(payload.seed, 0),
    };
    if (welcome.snapshotRate > 0) this.snapshotIntervalMs = 1000 / welcome.snapshotRate;

    const first = this.welcome === null;
    this.welcome = welcome;

    // The FIRST Welcome answers the Hello and carries no room; the join request
    // goes out on the back of it. A later one is the answer to that join.
    if (first && welcome.code === null) {
      this.sendJoin(this.joinPayload());
      return;
    }
    if (welcome.code !== null) this.enterRoom();
  }

  private joinPayload(): JoinRoomPayload {
    return {
      code: this.intent.code,
      mode: this.intent.mode,
      create: this.intent.create,
      spectator: this.intent.spectator === true,
      ...(this.intent.team === undefined ? {} : { team: this.intent.team }),
      reconnect: this.ticket,
    };
  }

  private sendJoin(payload: JoinRoomPayload): void {
    this.send(ClientMessage.JoinRoom, payload);
  }

  private onRoomState(payload: unknown): void {
    const state = readRoomState(payload);
    if (state === null) {
      this.counters.malformed++;
      return;
    }
    this.room = state;
    this.queue = null;

    const me = this.localPlayer;
    if (me !== null && Number.isFinite(me.ping) && me.ping > 0) this.serverRtt = me.ping;

    this.listener.onRoomState?.(state);
    this.enterRoom();
  }

  /**
   * Move into the room, and into the match when it is running.
   *
   * Both halves need Welcome AND RoomState — the seed and the seat come from
   * one, the roster, mode and mutators from the other — and the two arrive back
   * to back in either order depending on whether this was a join or a
   * reconnect. Calling this from both handlers is what makes the order not
   * matter.
   */
  private enterRoom(): void {
    const welcome = this.welcome;
    const room = this.room;
    if (welcome === null || welcome.code === null || room === null) return;
    if (this.state === 'closed') return;

    // A team switch is a leave and a re-join (see `switchTeam`), so the seat can
    // change under a live session. The prediction is built around ONE car index
    // and cannot be repointed, so a changed seat is a new world.
    if (this.session === null || this.session.seatIndex !== welcome.carIndex) {
      this.session = new NetWorldView(this, welcome, room);
    } else {
      this.session.setRoster(room);
    }

    if (room.inProgress) {
      if (this.state !== 'playing') {
        this.setPhase('playing');
        if (!this.started) {
          this.started = true;
          this.listener.onMatchStart?.();
        }
      }
    } else if (this.state !== 'lobby') {
      this.setPhase('lobby');
    }
  }

  private onSnapshot(raw: Snapshot): void {
    // A delta whose base has aged out cannot be completed. Drop it; the next
    // keyframe repairs everything. See SnapshotHistory.resolve.
    const complete = this.history.resolve(raw);
    if (complete === null) {
      this.counters.droppedSnapshots++;
      return;
    }

    const now = this.now();
    this.lastSnapshotAt = now;

    const sample = this.acks.elapsed(complete.ackedInputSeq, now);
    if (sample >= 0) {
      this.ackLatency =
        this.ackLatency < 0 ? sample : this.ackLatency + (sample - this.ackLatency) * RTT_SMOOTHING;
    }
    this.pushRtt();

    this.session?.onSnapshot(complete);
  }

  private onEvents(payload: unknown): void {
    if (!isRecord(payload) || !Array.isArray(payload.events)) {
      this.counters.malformed++;
      return;
    }
    const events = (payload as unknown as EventsPayload).events.filter(isNetEvent);
    if (events.length === 0) return;
    const tick = numberOr(payload.tick, 0);
    this.session?.pushEvents(events);
    this.listener.onEvents?.(events, tick);
  }

  /**
   * Answer a protocol-level ping.
   *
   * The WebSocket server never sends one — it uses RFC 6455 ping/pong, which a
   * browser answers below the application. This exists for the transport that
   * has no such frame, and it echoes the payload rather than inventing one so
   * the sender can match its own request without this end knowing the shape.
   */
  private onPing(payload: unknown): void {
    this.send(ClientMessage.Pong, payload ?? null);
    if (isRecord(payload) && typeof payload.rttMs === 'number' && payload.rttMs >= 0) {
      this.serverRtt = payload.rttMs;
      this.pushRtt();
    }
  }

  private onKick(payload: unknown): void {
    const kick = isRecord(payload) ? (payload as unknown as KickPayload) : null;
    const code = kick !== null && kick.code in KICK_FAILURES ? kick.code : null;
    this.finish(
      code === null ? 'bad-request' : KICK_FAILURES[code],
      typeof kick?.reason === 'string' ? kick.reason : ''
    );
    this.transport.close('kicked');
  }

  private onQueueStatus(payload: unknown): void {
    if (!isRecord(payload)) {
      this.counters.malformed++;
      return;
    }
    this.queue = {
      mode: this.intent.mode,
      waiting: numberOr(payload.waiting, 0),
      needed: numberOr(payload.needed, 0),
      waitedMs: numberOr(payload.waitedMs, 0),
    };
    if (this.state !== 'queued' && this.state !== 'closed') this.setPhase('queued');
    this.listener.onQueue?.(this.queue);
  }

  /**
   * The room went away, or refused us.
   *
   * NOT fatal to the connection: the socket is still good and the player can go
   * straight back to the multiplayer screen and try something else. Only the
   * SESSION ends. That is the difference between this and a kick, and it is why
   * a full room does not cost a reconnect.
   */
  private onRoomClosed(payload: unknown): void {
    const closed = isRecord(payload) ? (payload as unknown as RoomClosedPayload) : null;
    const reason = typeof closed?.reason === 'string' ? closed.reason : '';
    const failure: NetFailure =
      reason === 'room-full'
        ? 'room-full'
        : reason.includes('no such room')
          ? 'no-such-room'
          : 'room-closed';
    this.finish(failure, reason);
    this.transport.close(reason);
  }

  private onTransportClosed(reason: string): void {
    if (this.state === 'closed') return;
    // Before the handshake completed this is "the server is not there"; after
    // it, it is "the link went away mid-match". Both are the same event on the
    // socket and completely different sentences on screen.
    const beforeWelcome = this.welcome === null;
    this.finish(beforeWelcome ? 'refused' : 'lost', reason);
  }

  // -------------------------------------------------------------------------
  // Outbound
  // -------------------------------------------------------------------------

  private send(type: number, payload: unknown): void {
    if (this.state === 'closed') return;
    this.transport.send(encodeControl(type, payload));
  }

  /**
   * Send the newest input plus the previous `INPUT_REDUNDANCY - 1`.
   *
   * The redundancy is `PredictionClient.outboundInput`'s, not this file's: one
   * definition of "what a packet carries", so a lost packet costs nothing and
   * the client and the server agree on what "nothing" means.
   */
  sendInput(): void {
    const prediction = this.session?.prediction;
    if (prediction === null || prediction === undefined) return;
    if (this.state !== 'playing') return;
    const packet = prediction.outboundInput();
    if (packet.inputs.length === 0) return;
    this.acks.mark(packet.seq, this.now());
    this.counters.inputPackets++;
    this.transport.send(encodeInputPacket(packet));
  }

  private pushRtt(): void {
    const transport = this.transport as { setRtt?: (ms: number) => void };
    transport.setRtt?.(this.rttMs);
  }

  // -------------------------------------------------------------------------
  // Phase
  // -------------------------------------------------------------------------

  private setPhase(phase: NetPhase): void {
    if (this.state === phase) return;
    this.state = phase;
    this.listener.onPhase?.(phase);
  }

  /** End the session, exactly once, with a reason the UI can render. */
  private finish(failure: NetFailure | null, detail: string): void {
    if (this.state === 'closed') return;
    this.failed = failure;
    this.failureText =
      failure === null ? detail : failureMessage(failure, detail.slice(0, 120));
    this.setPhase('closed');
    this.listener.onClosed?.(failure, this.failureText);
  }
}

// ---------------------------------------------------------------------------
// The renderer's view of a networked world
// ---------------------------------------------------------------------------

/**
 * `WorldView` (src/main.ts), backed by the network.
 *
 * Structurally typed rather than declared as implementing it: `WorldView` lives
 * in main.ts, which has a boot side effect and must never be imported from a
 * library module. src/main.ts assigns one of these to a `WorldView` and tsc
 * checks the shape there, which is the only place it matters.
 *
 * ===========================================================================
 * WHAT IS DRAWN IS NOT WHAT IS SIMULATED
 * ===========================================================================
 *
 * The predicted world contains every car, because the local one collides with
 * them — but its remote cars are stepped with a GUESS (neutral input; this
 * client never sees anyone else's controller) and are wrong within a tick. So
 * `cars` is a separate array, filled every tick from two different sources:
 *
 *   local car    the predicted simulation, offset by whatever is left of the
 *                last correction (`PredictionClient.renderPose`)
 *   everyone     `SnapshotInterpolator`, one interpolation delay behind the
 *   else + ball  newest snapshot, and never extrapolated
 *
 * Writing the interpolated poses back into the predicted world instead would
 * be a tick cheaper and would poison the next rewind, because the replay would
 * re-simulate from positions that were never simulated.
 */
export class NetWorldView {
  /** The full simulation, ahead of the server. Rebuilt on a correction. */
  readonly prediction: PredictionClient | null;
  readonly interpolator = new SnapshotInterpolator();
  /** Index into `cars` this client drives, or null while spectating. */
  readonly seatIndex: number | null;

  private readonly sim: World;
  private readonly lead: TickLead;
  /** Smooth sample point for the interpolator — see its header for why the
   *  staircase of `renderTick()` cannot be sampled directly. */
  private readonly renderClock = new InterpolationClock();
  private readonly renderCars: CarState[];
  private readonly renderBall: BallState = makeBall();
  private readonly bodies: CarBody[];
  private readonly pending: NetEvent[] = [];
  private frame: InterpolatedFrame | null = null;
  private lastLead = 0;
  /** Goals the SERVER has reported. See `recordGoal`. */
  private serverGoals = 0;

  constructor(
    private readonly client: NetClient,
    welcome: WelcomeExtended,
    room: RoomState
  ) {
    // The seed, the roster size, the mutators and the match rules all come off
    // the wire, so the client's world is the server's world to the bit — which
    // is the entire premise prediction rests on. A guess at any one of them is
    // a client that corrects on every single snapshot.
    this.sim = new World({
      teamSize: room.teamSize,
      seed: welcome.seed,
      mutators: room.mutators,
      matchConfig: matchConfigForMode(room.mode),
    });

    this.seatIndex = welcome.carIndex;
    this.prediction =
      welcome.carIndex === null
        ? null
        : new PredictionClient(this.sim, { carIndex: welcome.carIndex });

    this.lead = new TickLead();
    this.renderCars = this.sim.cars.map(car => makeCar(car.team));
    this.bodies = this.sim.cars.map(() => 'octane' as CarBody);
    this.setRoster(room);
  }

  // -------------------------------------------------------------------------
  // WorldView
  // -------------------------------------------------------------------------

  get ball(): BallState {
    return this.renderBall;
  }

  get cars(): readonly CarState[] {
    return this.renderCars;
  }

  get match(): MatchState {
    return this.world.match;
  }

  get tick(): number {
    return this.world.tick;
  }

  get arena(): ArenaGeometry {
    return this.sim.arena;
  }

  padActive(index: number): boolean {
    return this.world.padActive(index);
  }

  /**
   * Analog powerslide, for the tyre marks.
   *
   * Zero for every car but the local one, and that is a wire fact rather than
   * an oversight: `CarSnapshot` carries six packed booleans and the powerslide
   * ramp is not one of them. Laying marks for a remote car would mean guessing
   * from its yaw rate, and a guess that draws on the pitch is worse than a mark
   * that is missing.
   */
  powerslide(index: number): number {
    const local = this.prediction?.carIndex;
    return local !== undefined && index === local ? this.world.powerslide(index) : 0;
  }

  /**
   * The server owns the replay.
   *
   * Returning null — "nothing was skipped" — is the honest answer and it is
   * what keeps the skip prompt from appearing: main.ts only treats a skip as
   * having happened when this returns a phase.
   */
  skipReplay(): MatchPhase | null {
    return null;
  }

  /**
   * Advance the prediction by however many ticks this slice of real time is
   * worth, then send the inputs it produced.
   *
   * `TickLead` decides the count, not the caller: the client runs a few percent
   * fast or slow to converge on a lead of RTT/2 + jitter, so a frame is
   * sometimes worth zero ticks and sometimes two. Setting the tick counter
   * directly instead would teleport every predicted body by the size of the
   * correction — see the header of ./prediction.
   */
  step(inputs: readonly CarInput[], dt: number): NetWorldEvents {
    const prediction = this.prediction;
    const serverTick = this.interpolator.newestTick;

    let ticks = 1;
    if (prediction !== null && serverTick >= 0) {
      ticks = this.lead.advance(dt, this.client.rttMs, prediction.tick, serverTick);
      this.lastLead = this.lead.lead;
    }

    if (prediction !== null) {
      const parallel = this.parallelInputs(inputs);
      for (let i = 0; i < ticks; i++) prediction.step(parallel);
      if (ticks > 0) this.client.sendInput();
    }

    // The sample point advances with REAL time, not with snapshot arrivals —
    // remote bodies must move every frame, not 30 times a second.
    this.sample(
      this.renderClock.advance(dt, this.interpolator.renderTick(), this.interpolator.newestTick)
    );
    return this.drainEvents();
  }

  // -------------------------------------------------------------------------
  // Wiring from NetClient
  // -------------------------------------------------------------------------

  /** The car body each seat is driving, from the roster. */
  setRoster(room: RoomState): void {
    for (const player of room.players) {
      const at = player.carIndex;
      if (at === null || at < 0 || at >= this.bodies.length) continue;
      const body = CAR_BODIES.includes(player.car) ? player.car : 'octane';
      if (this.bodies[at] === body) continue;
      this.bodies[at] = body;
      // The predicted world must use the same hitbox the server does, or the
      // local car's collisions diverge on the first contact. `config` is a
      // frozen shared singleton, so this is a reference swap.
      this.sim.cars[at].config = carConfigFor(body);
      this.renderCars[at].config = carConfigFor(body);
    }
  }

  /** The body driving seat `index`, for building its mesh. */
  bodyFor(index: number): CarBody {
    return this.bodies[index] ?? 'octane';
  }

  onSnapshot(snap: Snapshot): void {
    this.interpolator.push(snap);
    if (this.prediction !== null) {
      this.prediction.onSnapshot(snap);
    } else {
      // Spectating: there is nothing to predict, so the authority IS the world.
      // Everything drawn still comes from the interpolator; this only keeps the
      // match clock, the score and the boost pads honest.
      applySnapshot(this.sim, snap);
    }
    // Refresh at the clock's CURRENT tick without advancing it — time only
    // passes in `step`. Before the first step the clock is unset, so fall back
    // to the interpolator's own notion; the sample clamps into the buffer.
    this.sample(this.renderClock.tick >= 0 ? this.renderClock.tick : this.interpolator.renderTick());
  }

  pushEvents(events: readonly NetEvent[]): void {
    for (const event of events) {
      // Bounded: a stalled renderer must not accumulate a match of events.
      if (this.pending.length >= 128) this.pending.shift();
      this.pending.push(event);
    }
  }

  get predictionStats(): { snapshots: number; corrections: number; replayedTicks: number; resets: number } | null {
    return this.prediction?.stats ?? null;
  }

  get leadTicks(): number {
    return this.lastLead;
  }

  get targetLeadTicks(): number {
    return this.lead.lastTarget;
  }

  get resyncCount(): number {
    return this.lead.resyncCount;
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  /** The live simulation. Read fresh — a correction replaces the object. */
  private get world(): World {
    return this.prediction === null ? this.sim : this.prediction.world;
  }

  /**
   * The full input array a prediction tick needs.
   *
   * The local seat gets the player's input; every other seat gets neutral,
   * because this client has never seen anyone else's controller and will not
   * until the snapshot that already contains the result. A wrong guess costs a
   * correction, which is exactly what the reconciler is for, and the guess is
   * recorded so a replay reproduces it rather than drifting from it.
   */
  private parallelInputs(inputs: readonly CarInput[]): CarInput[] {
    const local = this.prediction?.carIndex ?? -1;
    return this.renderCars.map((_, i) => (i === local ? (inputs[local] ?? neutralInput()) : neutralInput()));
  }

  /** Fill the render array from the interpolator and the prediction. */
  private sample(tick: number): void {
    const frame = this.interpolator.sample(tick);
    if (frame !== null) this.frame = frame;
    const held = this.frame;

    const local = this.prediction?.carIndex ?? -1;
    for (let i = 0; i < this.renderCars.length; i++) {
      if (i === local) this.fillLocal(this.renderCars[i]);
      else if (held !== null && held.cars[i] !== undefined) fillFromFrame(this.renderCars[i], held.cars[i]);
    }

    if (held !== null) {
      copyTriple(this.renderBall.position, held.ball.position);
      copyTriple(this.renderBall.velocity, held.ball.velocity);
      // The renderer spins the ball mesh from this, and nothing else does:
      // there is no orientation on the wire to fall back on.
      copyTriple(this.renderBall.angularVelocity, held.ball.angularVelocity);
    }
  }

  private fillLocal(into: CarState): void {
    const prediction = this.prediction;
    if (prediction === null) return;
    const pose = prediction.renderPose();
    const car = prediction.world.cars[prediction.carIndex];
    into.position = pose.position;
    into.orientation = pose.orientation;
    into.velocity = cloneVec(car.velocity);
    into.angularVelocity = cloneVec(car.angularVelocity);
    into.boostAmount = car.boostAmount;
    into.onGround = car.onGround;
    into.supersonic = car.supersonic;
    into.demolished = car.demolished;
    into.hasFlip = car.hasFlip;
    into.isDodging = car.isDodging;
    into.hasJumped = car.hasJumped;
  }

  /**
   * Turn the server's events into the record the renderer already consumes.
   *
   * This is the point of `NetEvent` existing at all: a goal, a demolition, a
   * bump and a touch are FACTS the server states, not conclusions the client
   * draws by diffing two snapshots. Diffing gets the ball-vs-arena bounce right
   * and the goal wrong — a snapshot 33 ms after the ball crossed shows a
   * stationary ball at the centre spot and no evidence a goal ever happened.
   */
  private drainEvents(): NetWorldEvents {
    const out: NetWorldEvents = {
      goal: null,
      hits: [],
      kickoff: false,
      frozen: this.match.frozen,
      touches: 0,
    };
    if (this.pending.length === 0) return out;

    for (const event of this.pending) {
      switch (event.kind) {
        case 'goal':
          out.goal = { team: event.team, speed: event.speed };
          this.recordGoal(event.team, event.scorer, event.speed);
          break;
        case 'kickoff':
          out.kickoff = true;
          break;
        case 'ballTouch':
          out.touches++;
          out.hits.push({ position: cloneVec(this.renderBall.position), strength: event.speed });
          break;
        default:
          // demolition, bump, boostPickup and matchEnded carry no render event
          // in `WorldEvents`; the state they produce is already in the snapshot.
          break;
      }
    }
    this.pending.length = 0;
    return out;
  }

  /**
   * Keep `MatchState.goals` in step with what the server has reported.
   *
   * The predicted world detects its own goals and appends them, so most of the
   * time there is nothing to do — and `MatchSnapshot` carries the list, so a
   * rewind restores it rather than losing it. But a client corrected ACROSS the
   * moment of the goal never runs that tick, and both the HUD banner and the
   * replay card read the last entry.
   *
   * Counting what the server said, and topping the list up to that count, is
   * the one rule that is right in both directions: it cannot double-count a
   * goal the prediction already logged, and it cannot leave the banner empty
   * for one it missed.
   */
  private recordGoal(team: 0 | 1, scorer: number | null, speed: number): void {
    this.serverGoals++;
    const match = this.match;
    while (match.goals.length < this.serverGoals) {
      match.goals.push({ team, scorer, clockRemaining: match.clock, speed });
    }
  }
}

// ---------------------------------------------------------------------------
// The event record the renderer consumes
// ---------------------------------------------------------------------------

/** Structurally `BallHitEvent` in src/main.ts. */
export interface NetBallHit {
  position: Vec3;
  strength: number;
}

/** Structurally `WorldEvents` in src/main.ts. */
export interface NetWorldEvents {
  goal: { team: 0 | 1; speed: number } | null;
  hits: NetBallHit[];
  kickoff: boolean;
  frozen: boolean;
  touches: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function numberOr(value: unknown, fallback: number): number;
function numberOr(value: unknown, fallback: null): number | null;
function numberOr(value: unknown, fallback: number | null): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

const copyTriple = (into: Vec3, from: Vec3): void => {
  into.x = from.x;
  into.y = from.y;
  into.z = from.z;
};

const fillFromFrame = (into: CarState, from: InterpolatedCar): void => {
  into.position = from.position;
  into.velocity = from.velocity;
  into.angularVelocity = from.angularVelocity;
  into.orientation = from.orientation;
  into.boostAmount = from.boost;
  into.onGround = (from.flags & FLAG_ON_GROUND) !== 0;
  into.supersonic = (from.flags & FLAG_SUPERSONIC) !== 0;
  into.demolished = (from.flags & FLAG_DEMOLISHED) !== 0;
  into.hasFlip = (from.flags & FLAG_HAS_FLIP) !== 0;
  into.isDodging = (from.flags & FLAG_IS_DODGING) !== 0;
  into.hasJumped = (from.flags & FLAG_JUMPED) !== 0;
};

/**
 * A `RoomState` off the wire, or null.
 *
 * Everything here is JSON somebody else wrote, so it is checked rather than
 * cast. A roster with one malformed row loses that row rather than the whole
 * message: a lobby that goes blank because one player's name arrived as a
 * number is a worse failure than a lobby missing a line.
 */
export const readRoomState = (payload: unknown): RoomState | null => {
  if (!isRecord(payload)) return null;
  if (typeof payload.code !== 'string' || !isMultiplayerMode(payload.mode)) return null;
  if (!Array.isArray(payload.players)) return null;

  const players: RoomPlayer[] = [];
  for (const raw of payload.players) {
    if (!isRecord(raw)) continue;
    if (typeof raw.playerId !== 'number') continue;
    players.push({
      playerId: raw.playerId,
      displayName: typeof raw.displayName === 'string' ? raw.displayName : 'Player',
      team: raw.team === 1 ? 1 : 0,
      carIndex: typeof raw.carIndex === 'number' ? raw.carIndex : null,
      car: CAR_BODIES.includes(raw.car as CarBody) ? (raw.car as CarBody) : 'octane',
      ready: raw.ready === true,
      isBot: raw.isBot === true,
      ping: typeof raw.ping === 'number' && Number.isFinite(raw.ping) ? raw.ping : 0,
    });
  }

  return {
    code: payload.code,
    mode: payload.mode,
    teamSize: Math.max(1, Math.min(4, Math.round(numberOr(payload.teamSize, 3)))),
    players,
    // Handed straight to `World`, which normalizes any row it does not
    // recognise back to its default — see `normalizeMutators`. A missing block
    // is the unmutated game, which is also what a server that sent none meant.
    mutators: isRecord(payload.mutators)
      ? (payload.mutators as unknown as MutatorConfig)
      : defaultMutators(),
    inProgress: payload.inProgress === true,
  };
};

const NET_EVENT_KINDS: readonly string[] = [
  'goal',
  'demolition',
  'bump',
  'ballTouch',
  'boostPickup',
  'kickoff',
  'matchEnded',
];

const isNetEvent = (value: unknown): value is NetEvent =>
  isRecord(value) && typeof value.kind === 'string' && NET_EVENT_KINDS.includes(value.kind);
